/**
 * InvitationManager
 * Component for managing workspace invitations
 *
 * Features:
 * - List pending/all invitations
 * - Filter by status
 * - View invitation details
 * - Resend/revoke invitations (admin only)
 */

import React, { useState, useMemo, useCallback } from 'react';
import { useTheme } from '../../utils/theme';
import { useWorkspace } from './WorkspaceProvider';
import { InviteStaffModal } from './modals/InviteStaffModal';
import { cn } from '../design-system';
import { Button } from '../v2/ui/button';
import {
  Mail,
  Search,
  Plus,
  Clock,
  Check,
  X,
  AlertTriangle,
  Calendar,
  User,
  RefreshCw,
  Loader2,
} from 'lucide-react';

// Status configurations
const STATUS_CONFIG = {
  PENDING: {
    icon: Clock,
    label: 'Pending',
    color: { dark: 'text-yellow-400 bg-yellow-500/20', light: 'text-yellow-700 bg-yellow-100' },
  },
  ACCEPTED: {
    icon: Check,
    label: 'Accepted',
    color: { dark: 'text-green-400 bg-green-500/20', light: 'text-green-700 bg-green-100' },
  },
  DECLINED: {
    icon: X,
    label: 'Declined',
    color: { dark: 'text-red-400 bg-red-500/20', light: 'text-red-700 bg-red-100' },
  },
  EXPIRED: {
    icon: AlertTriangle,
    label: 'Expired',
    color: { dark: 'text-gray-400 bg-gray-500/20', light: 'text-gray-600 bg-gray-100' },
  },
  REVOKED: {
    icon: X,
    label: 'Revoked',
    color: { dark: 'text-gray-500 bg-gray-500/20', light: 'text-gray-500 bg-gray-100' },
  },
};

/**
 * Format date for display
 */
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

/**
 * Format relative time
 */
const formatRelativeTime = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = date - now;
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays < 0) return 'Expired';
  if (diffDays === 0) return 'Expires today';
  if (diffDays === 1) return 'Expires tomorrow';
  return `Expires in ${diffDays} days`;
};

/**
 * InvitationRow component
 */
const InvitationRow = ({ invitation, isDarkMode, getPersonaById }) => {
  const persona = getPersonaById(invitation.personaId);
  const statusConfig = STATUS_CONFIG[invitation.status] || STATUS_CONFIG.PENDING;
  const StatusIcon = statusConfig.icon;

  return (
    <div
      className={cn(
        'flex items-center gap-4 p-4 rounded-lg border transition-colors',
        'bg-card/30 border-border/50'
      )}
    >
      {/* Email Icon */}
      <div className={cn(
        'w-10 h-10 rounded-lg flex items-center justify-center',
        'bg-primary/10'
      )}>
        <Mail className={cn(
          'w-5 h-5',
          'text-primary'
        )} />
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <h4 className={cn(
            'font-medium truncate',
            'text-white'
          )}>
            {invitation.email}
          </h4>
        </div>
        <div className="flex items-center gap-3 mt-0.5">
          {invitation.name && (
            <span className={cn(
              'flex items-center gap-1 text-sm',
              'text-gray-400'
            )}>
              <User className="w-3.5 h-3.5" />
              {invitation.name}
            </span>
          )}
          <span className={cn(
            'text-sm',
            'text-gray-500'
          )}>
            {persona?.name || 'Unknown Role'}
          </span>
        </div>
      </div>

      {/* Dates */}
      <div className="text-right hidden sm:block">
        <div className={cn(
          'flex items-center gap-1 text-xs',
          'text-gray-500'
        )}>
          <Calendar className="w-3 h-3" />
          Sent {formatDate(invitation.sentAt)}
        </div>
        {invitation.status === 'PENDING' && (
          <div className={cn(
            'text-xs mt-0.5',
            new Date(invitation.expiresAt) < new Date()
              ? 'text-red-400'
              : 'text-gray-400'
          )}>
            {formatRelativeTime(invitation.expiresAt)}
          </div>
        )}
      </div>

      {/* Status Badge */}
      <div className={cn(
        'flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium',
        statusConfig.color.dark
      )}>
        <StatusIcon className="w-3 h-3" />
        {statusConfig.label}
      </div>
    </div>
  );
};

/**
 * InvitationManager component
 */
export function InvitationManager() {
  const { theme } = useTheme();
  const isDarkMode = true; // OLED: always dark

  const {
    invitations,
    isAdmin,
    refreshWorkspace,
    getPersonaById,
    currentWorkspace,
  } = useWorkspace();

  // State
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Filter options
  const filterOptions = [
    { value: 'all', label: 'All' },
    { value: 'PENDING', label: 'Pending' },
    { value: 'ACCEPTED', label: 'Accepted' },
    { value: 'DECLINED', label: 'Declined' },
    { value: 'EXPIRED', label: 'Expired' },
  ];

  // Filtered invitations
  const filteredInvitations = useMemo(() => {
    return invitations.filter(inv => {
      // Status filter
      if (statusFilter !== 'all' && inv.status !== statusFilter) {
        return false;
      }

      // Search filter
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        const email = (inv.email || '').toLowerCase();
        const name = (inv.name || '').toLowerCase();
        const persona = getPersonaById(inv.personaId);
        const role = (persona?.name || '').toLowerCase();

        if (!email.includes(query) && !name.includes(query) && !role.includes(query)) {
          return false;
        }
      }

      return true;
    });
  }, [invitations, statusFilter, searchQuery, getPersonaById]);

  // Stats
  const stats = useMemo(() => ({
    total: invitations.length,
    pending: invitations.filter(i => i.status === 'PENDING').length,
    accepted: invitations.filter(i => i.status === 'ACCEPTED').length,
    expired: invitations.filter(i => i.status === 'EXPIRED' || i.status === 'DECLINED').length,
  }), [invitations]);

  // Handle refresh
  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    try {
      await refreshWorkspace();
    } finally {
      setIsRefreshing(false);
    }
  }, [refreshWorkspace]);

  // Handle invite success
  const handleInviteSuccess = useCallback(() => {
    setIsInviteModalOpen(false);
    refreshWorkspace();
  }, [refreshWorkspace]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className={cn(
            'text-xl font-semibold',
            'text-white'
          )}>
            Invitations
          </h2>
          <p className={cn(
            'text-sm',
            'text-gray-400'
          )}>
            {stats.pending} pending, {stats.accepted} accepted
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={isRefreshing}
          >
            <RefreshCw className={cn(
              'w-4 h-4',
              isRefreshing && 'animate-spin'
            )} />
          </Button>
          {isAdmin && (
            <Button onClick={() => setIsInviteModalOpen(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Send Invite
            </Button>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Total', value: stats.total, color: 'text-blue-400' },
          { label: 'Pending', value: stats.pending, color: 'text-yellow-400' },
          { label: 'Accepted', value: stats.accepted, color: 'text-green-400' },
          { label: 'Expired/Declined', value: stats.expired, color: 'text-gray-400' },
        ].map((stat) => (
          <div
            key={stat.label}
            className={cn(
              'p-3 rounded-lg',
              'bg-card/30 border border-border/50'
            )}
          >
            <p className={cn(
              'text-xs mb-1',
              'text-gray-500'
            )}>
              {stat.label}
            </p>
            <p className={cn(
              'text-2xl font-semibold',
              stat.color
            )}>
              {stat.value}
            </p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        {/* Search */}
        <div className="relative flex-1">
          <Search className={cn(
            'absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4',
            'text-gray-500'
          )} />
          <input
            type="text"
            placeholder="Search by email, name, or role..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className={cn(
              'w-full pl-10 pr-4 py-2.5 rounded-lg border text-sm outline-none transition-colors',
              'bg-card/50 border-border/50 text-white placeholder-gray-500 focus:border-primary/50'
            )}
          />
        </div>

        {/* Status Filter */}
        <div className="flex gap-1 p-1 rounded-lg bg-card/30 border border-border/50">
          {filterOptions.map((option) => (
            <button
              key={option.value}
              onClick={() => setStatusFilter(option.value)}
              className={cn(
                'px-3 py-1.5 rounded text-sm font-medium transition-colors',
                statusFilter === option.value
                  ? 'bg-primary text-black'
                  : 'text-gray-400 hover:text-white'
              )}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {/* Invitations List */}
      <div className="space-y-2">
        {filteredInvitations.length > 0 ? (
          filteredInvitations.map((invitation) => (
            <InvitationRow
              key={invitation.id}
              invitation={invitation}
              isDarkMode={isDarkMode}
              getPersonaById={getPersonaById}
            />
          ))
        ) : invitations.length === 0 ? (
          <div className={cn(
            'text-center py-12',
            'text-gray-500'
          )}>
            <Mail className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p className="mb-4">No invitations sent yet</p>
            {isAdmin && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsInviteModalOpen(true)}
              >
                <Plus className="w-4 h-4 mr-2" />
                Send First Invitation
              </Button>
            )}
          </div>
        ) : (
          <div className={cn(
            'text-center py-12',
            'text-gray-500'
          )}>
            No invitations match your filters
          </div>
        )}
      </div>

      {/* Invite Modal */}
      <InviteStaffModal
        isOpen={isInviteModalOpen}
        onClose={() => setIsInviteModalOpen(false)}
        onSuccess={handleInviteSuccess}
      />
    </div>
  );
}

export default InvitationManager;
